import React, { useState, useEffect } from 'react';

const DownloadMASTIVIBESApp = () => {
  const [animatedTitle, setAnimatedTitle] = useState('');
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isPhoneHovered, setIsPhoneHovered] = useState(false);

  const fullTitle = "Download MASTIVIBES App";
  
  const features = [
    {
      icon: '👥',
      title: 'Create Groups',
      description: 'Build custom communities with unique fields and settings',
      gradient: 'from-pink-500 to-purple-500'
    },
    {
      icon: '📹',
      title: 'Group Video Calls',
      description: 'HD video calls with up to 100 participants and screen sharing',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: '🎨',
      title: 'Custom Profiles',
      description: 'Design unique profiles with personalized fields and themes',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: '💬',
      title: 'Real-time Chat',
      description: 'Instant messaging, voice rooms, and collaborative tools',
      gradient: 'from-green-500 to-teal-500'
    }
  ];

  const appStats = [
    { number: '4.9', label: 'App Store Rating', icon: '⭐' },
    { number: '500K+', label: 'Downloads', icon: '📥' },
    { number: '98%', label: 'Satisfaction', icon: '😊' },
    { number: '24/7', label: 'Active Users', icon: '👥' }
  ];

  // Social platform floating icons
  const socialFloatingIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-20 left-20', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-32 right-32', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-32 left-32', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-20 right-20', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/2 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setAnimatedTitle(fullTitle.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(featureInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 py-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.1}s`,
              opacity: 0.7
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${item.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${6 + index}s`
          }}
        >
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Animated Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30 mb-8 shadow-lg">
            <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-md"></span>
            <span className="text-white font-bold">Available on iOS, Android</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
              {animatedTitle}
            </span>
            <span className="animate-pulse text-yellow-300">|</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            Connect, create, and celebrate anywhere with the ultimate social platform app. 
            Experience unlimited customization and real-time connections on the go.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`bg-white/15 backdrop-blur-2xl rounded-2xl p-6 border-2 border-white/25 transition-all duration-500 transform ${
                    index === currentFeature ? 'scale-105 bg-white/20 shadow-2xl' : 'scale-100'
                  } hover:scale-105 hover:bg-white/20 group shadow-lg`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center text-3xl text-white transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 ${index === currentFeature ? 'animate-pulse' : ''} shadow-2xl`}>
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg group-hover:text-yellow-300 transition-colors duration-300">
                        {feature.title}
                      </h4>
                      <p className="text-white/80 text-sm mt-1 font-medium">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  {index === currentFeature && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping shadow-lg"></div>
                  )}
                </div>
              ))}
            </div>

            {/* App Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {appStats.map((stat, index) => (
                <div 
                  key={index}
                  className="text-center p-4 bg-white/10 backdrop-blur-lg rounded-xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 group shadow-lg"
                >
                  <div className="text-3xl mb-2 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="text-white/80 text-xs mt-1 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Download Buttons */}
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* App Store Button */}
                <button className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-2xl blur opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center space-x-3 bg-black/80 hover:bg-black/90 text-white py-4 px-8 rounded-2xl transition-all duration-300 hover:scale-105 transform hover:-translate-y-1 border-2 border-white/30 backdrop-blur-lg shadow-2xl">
                    <div className="text-4xl">📱</div>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">Download on</div>
                      <div className="text-xl font-black">App Store</div>
                    </div>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-ping group-hover:animate-none shadow-md"></div>
                  </div>
                </button>

                {/* Google Play Button */}
                <button className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-green-600 rounded-2xl blur opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center space-x-3 bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white py-4 px-8 rounded-2xl transition-all duration-300 hover:scale-105 transform hover:-translate-y-1 border-2 border-white/30 backdrop-blur-lg shadow-2xl">
                    <div className="text-4xl">🤖</div>
                    <div className="text-left">
                      <div className="text-xs text-green-200">Get it on</div>
                      <div className="text-xl font-black">Google Play</div>
                    </div>
                    <div className="w-3 h-3 bg-white rounded-full animate-ping group-hover:animate-none shadow-md"></div>
                  </div>
                </button>
              </div>

              {/* QR Code Section */}
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl p-6 border-2 border-white/25 text-center shadow-xl">
                <h4 className="text-white font-bold mb-4 text-lg">Scan to Download</h4>
                <div className="bg-gradient-to-br from-yellow-500/30 to-pink-500/30 p-6 rounded-2xl inline-block transform hover:scale-105 transition-all duration-300 shadow-2xl">
                  <div className="w-40 h-40 bg-white rounded-xl flex items-center justify-center">
                    <div className="relative">
                      <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 via-pink-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-5xl">
                        📲
                      </div>
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping"></div>
                    </div>
                  </div>
                </div>
                <p className="text-white/70 text-sm mt-4 font-medium">
                  Scan with your camera to download instantly
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Phone Mockup */}
          <div className="relative">
            {/* Animated Phone Mockup */}
            <div 
              className={`relative mx-auto w-80 h-[600px] bg-gray-900 rounded-[3rem] border-[14px] border-gray-800 shadow-2xl transform transition-all duration-700 ${
                isPhoneHovered ? 'scale-110 rotate-3' : 'scale-100 rotate-0'
              }`}
              onMouseEnter={() => setIsPhoneHovered(true)}
              onMouseLeave={() => setIsPhoneHovered(false)}
            >
              {/* Phone Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-6 bg-gray-800 rounded-b-2xl z-20"></div>
              
              {/* Phone Screen */}
              <div className="absolute inset-4 bg-gradient-to-br from-yellow-500/10 via-pink-500/10 to-orange-500/10 rounded-3xl overflow-hidden backdrop-blur-lg border-2 border-white/20">
                {/* App Interface Simulation */}
                <div className="p-6 h-full flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-lg">✨</span>
                      </div>
                      <span className="text-white font-black text-lg">MASTIVIBES</span>
                    </div>
                    <div className="text-white text-sm font-bold">12:30</div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 space-y-4">
                    {/* Welcome Message */}
                    <div className="bg-gradient-to-r from-yellow-500/20 to-pink-500/20 rounded-2xl p-4 backdrop-blur-sm border border-white/20">
                      <h3 className="text-white font-bold text-lg mb-2">Welcome Back!</h3>
                      <p className="text-white/80 text-sm">Your communities are waiting! 🎉</p>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: '👥', label: 'Groups', color: 'from-pink-500 to-purple-500' },
                        { icon: '📹', label: 'Video Call', color: 'from-cyan-500 to-blue-500' },
                        { icon: '🎨', label: 'Customize', color: 'from-yellow-500 to-orange-500' },
                        { icon: '🎵', label: 'Voice Room', color: 'from-green-500 to-teal-500' }
                      ].map((action, index) => (
                        <div 
                          key={index}
                          className={`bg-gradient-to-r ${action.color} rounded-xl p-3 text-center transform hover:scale-105 transition-all duration-300 cursor-pointer shadow-lg`}
                        >
                          <div className="text-3xl mb-2">{action.icon}</div>
                          <div className="text-white text-xs font-bold">{action.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Active Communities */}
                    <div className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm border border-white/20">
                      <h4 className="text-white font-bold mb-3 flex items-center">
                        <span className="mr-2">👥</span>
                        Active Communities
                      </h4>
                      <div className="space-y-2">
                        {['Music Lovers Club', 'Remote Workers Hub', 'Gaming Squad', 'Book Readers'].map((item, index) => (
                          <div key={index} className="flex items-center justify-between text-white/80 text-sm">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse mr-2"></div>
                              <span>{item}</span>
                            </div>
                            <div className="text-xs bg-white/10 px-2 py-1 rounded-full">
                              {['45', '128', '89', '23'][index]} online
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Group Call Status */}
                    <div className="bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-2xl p-4 backdrop-blur-sm border border-white/20">
                      <h4 className="text-white font-bold mb-3 flex items-center">
                        <span className="mr-2">📹</span>
                        Active Group Call
                      </h4>
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <span className="text-white text-lg">👥</span>
                          </div>
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
                        </div>
                        <div>
                          <div className="text-white font-semibold">Creative Minds</div>
                          <div className="text-cyan-300 text-xs">12 participants • Live now</div>
                        </div>
                        <button className="ml-auto bg-white text-pink-600 px-3 py-1 rounded-lg text-sm font-bold hover:scale-105 transition-transform">
                          Join
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Bar */}
                  <div className="flex justify-around pt-4 border-t border-white/20">
                    {['🏠', '🔍', '👥', '💬', '⚙️'].map((icon, index) => (
                      <button 
                        key={index} 
                        className="text-3xl text-gray-400 hover:text-yellow-300 transition-all duration-300 hover:scale-110"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Home Indicator */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/50 rounded-full"></div>
            </div>

            {/* Floating Elements Around Phone */}
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-yellow-500/30 rounded-full blur-xl animate-bounce shadow-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-pink-500/30 rounded-full blur-xl animate-bounce delay-1000 shadow-2xl"></div>
            
            {/* App Icons Floating */}
            <div className="absolute -top-4 left-10 w-14 h-14 bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow shadow-xl">
              👥
            </div>
            <div className="absolute top-20 -right-4 w-12 h-12 bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-xl animate-float-slow delay-500 shadow-xl">
              📹
            </div>
            <div className="absolute bottom-20 -left-4 w-13 h-13 bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-xl animate-float-slow delay-1000 shadow-xl">
              💬
            </div>
          </div>
        </div>

        {/* Platform Features Badges */}
        <div className="mt-20 text-center">
          <div className="bg-white/15 backdrop-blur-xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-8">
              Why <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">MASTIVIBES</span> App?
            </h3>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: '👥', text: 'Unlimited Groups', color: 'bg-pink-500/20' },
                { icon: '🎨', text: 'Custom Fields', color: 'bg-yellow-500/20' },
                { icon: '📹', text: 'HD Video Calls', color: 'bg-cyan-500/20' },
                { icon: '💬', text: 'Real-time Chat', color: 'bg-green-500/20' },
                { icon: '🎵', text: 'Voice Rooms', color: 'bg-purple-500/20' },
                { icon: '⚡', text: 'Always Online', color: 'bg-orange-500/20' }
              ].map((badge, index) => (
                <div 
                  key={index}
                  className={`flex items-center space-x-3 ${badge.color} backdrop-blur-sm rounded-xl px-6 py-4 border-2 border-white/30 hover:scale-105 transition-all duration-300 group shadow-lg`}
                >
                  <span className="text-3xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">{badge.icon}</span>
                  <span className="text-white font-bold text-lg">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Removed Cross-Platform Support Section */}
        
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-15px) rotate(3deg) scale(1.1); 
          }
          66% { 
            transform: translateY(8px) rotate(-2deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default DownloadMASTIVIBESApp;